import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="mb-8">
            <Link to="/" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
              ← Back to Home
            </Link>
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Terms of Service</h1>
          <p className="text-gray-600 mb-6">Last updated: {new Date().toLocaleDateString()}</p>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 mb-4">
                By accessing and using Flash AI's Business Continuity Planning platform ("Service"), 
                you accept and agree to be bound by the terms and provision of this agreement. If you 
                do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="text-gray-700 mb-4">
                Flash AI provides an AI-powered platform for creating Business Continuity Plans
                aligned with ISO 22301:2019. Our service includes:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>AI-generated business continuity plan templates</li>
                <li>Risk assessment and impact analysis tools</li>
                <li>Document generation and management</li>
                <li>Tabletop exercise planning and execution</li>
                <li>Compliance monitoring and reporting</li>
                <li>Training and educational resources</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts</h2>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Account Creation</h3>
              <p className="text-gray-700 mb-4">
                To use our service, you must create an account by providing accurate and complete 
                information. You are responsible for maintaining the confidentiality of your account 
                credentials and for all activities that occur under your account.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Account Security</h3>
              <p className="text-gray-700 mb-4">
                You must notify us immediately of any unauthorized use of your account or any other 
                breach of security. We are not liable for any loss or damage arising from your failure 
                to comply with this security obligation.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Subscription Plans and Payment</h2>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 Subscription Tiers</h3>
              <p className="text-gray-700 mb-4">
                We offer three subscription tiers:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Essential:</strong> $799/year - Basic BCP generation and incident response tools</li>
                <li><strong>Gold:</strong> $1,999/year - Essential features plus Flash RTC and dedicated support</li>
                <li><strong>Premium:</strong> $3,999/year - Complete solution with executive training and a resilience badge</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2 Payment Terms</h3>
              <p className="text-gray-700 mb-4">
                Subscription fees are billed annually in advance. All fees are non-refundable except 
                as required by law. We reserve the right to change our pricing with 30 days' notice.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.3 Automatic Renewal</h3>
              <p className="text-gray-700 mb-4">
                Subscriptions automatically renew unless cancelled before the renewal date. You may 
                cancel your subscription at any time through your account settings.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Acceptable Use</h2>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 Permitted Use</h3>
              <p className="text-gray-700 mb-4">
                You may use our service only for lawful purposes and in accordance with these terms. 
                You agree to use the service only for creating legitimate business continuity plans 
                for your organization.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.2 Prohibited Activities</h3>
              <p className="text-gray-700 mb-4">You agree not to:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe on intellectual property rights</li>
                <li>Transmit harmful or malicious code</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Use the service for any illegal or unauthorized purpose</li>
                <li>Interfere with or disrupt the service or servers</li>
                <li>Resell or redistribute the service without permission</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Intellectual Property</h2>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">6.1 Our Rights</h3>
              <p className="text-gray-700 mb-4">
                The service and its original content, features, and functionality are owned by Flash AI 
                and are protected by international copyright, trademark, patent, trade secret, and other 
                intellectual property laws.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">6.2 Your Content</h3>
              <p className="text-gray-700 mb-4">
                You retain ownership of the business data and content you input into our service. By 
                using our service, you grant us a limited license to process, store, and analyze your 
                data solely for the purpose of providing our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Privacy and Data Protection</h2>
              <p className="text-gray-700 mb-4">
                Your privacy is important to us. Our collection and use of personal information in 
                connection with the service is governed by our Privacy Policy, which is incorporated 
                into these terms by reference.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Service Availability</h2>
              <p className="text-gray-700 mb-4">
                We strive to maintain high service availability but do not guarantee uninterrupted access. 
                We may temporarily suspend the service for maintenance, updates, or other operational reasons.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Disclaimers and Limitations</h2>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">9.1 Service Disclaimer</h3>
              <p className="text-gray-700 mb-4">
                The service is provided "as is" without warranties of any kind. We do not guarantee 
                that the AI-generated content will be accurate, complete, or suitable for your specific 
                business needs.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">9.2 BCMS Software Disclaimers</h3>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <h4 className="text-lg font-semibold text-yellow-800 mb-2">Disclaimer of Certification Guarantee</h4>
                <p className="text-yellow-700 mb-4">
                  Flash AI provides Business Continuity and Disaster Recovery planning tools aligned with 
                  ISO 22301:2019 and ISO 27001 best practices. However, purchase or use of this software 
                  does not guarantee certification or approval by any standards body, auditor, or regulatory authority.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
                <h4 className="text-lg font-semibold text-red-800 mb-2">Insurance and Regulatory Limitations</h4>
                <p className="text-red-700 mb-4">
                  Use of this software and the resulting plans does not guarantee acceptance, approval, 
                  or premium discounts from insurers or compliance with regulatory agencies. Customers remain 
                  responsible for engaging directly with insurers and regulators regarding eligibility or 
                  compliance determinations.
                </p>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                <h4 className="text-lg font-semibold text-blue-800 mb-2">Responsibility for Implementation</h4>
                <p className="text-blue-700 mb-4">
                  The software generates templates and recommendations. Customers are solely responsible for 
                  reviewing, customizing, and implementing their Business Continuity and Disaster Recovery Plans, 
                  and for ensuring their accuracy and adequacy for their specific operations.
                </p>
              </div>

              <div className="bg-gray-50 border-l-4 border-gray-400 p-4 mb-4">
                <h4 className="text-lg font-semibold text-gray-800 mb-2">No Liability for Certification or Audit Outcomes</h4>
                <p className="text-gray-700 mb-4">
                  Flash AI shall not be liable for any adverse findings, failed certifications, regulatory 
                  penalties, or denied insurance claims related to the use of the software or the plans it generates.
                </p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
                <h4 className="text-lg font-semibold text-green-800 mb-2">Encouragement of Independent Validation</h4>
                <p className="text-green-700 mb-4">
                  Customers are strongly encouraged to validate their plans through independent audits, 
                  tabletop exercises, and insurer or regulator reviews to ensure adequacy for their 
                  organizational requirements.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">9.3 Limitation of Liability</h3>
              <p className="text-gray-700 mb-4">
                To the maximum extent permitted by law, Flash AI shall not be liable for any indirect, 
                incidental, special, consequential, or punitive damages resulting from your use of the service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Termination</h2>
              <p className="text-gray-700 mb-4">
                We may terminate or suspend your account immediately, without prior notice, for conduct 
                that we believe violates these terms or is harmful to other users, us, or third parties.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Governing Law</h2>
              <p className="text-gray-700 mb-4">
                These terms shall be governed by and construed in accordance with the laws of the State 
                of Delaware, without regard to conflict of law principles.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to Terms</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to modify these terms at any time. We will notify users of any 
                material changes via email or through the service. Your continued use of the service 
                after such modifications constitutes acceptance of the updated terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>Email:</strong> legal@flashai.com<br />
                  <strong>Address:</strong> Flash AI, 123 Business Street, Suite 100, City, State 12345<br />
                  <strong>Phone:</strong> +1 (555) 123-4567
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
